import { useLayoutEffect, useRef } from 'react'
import { REPLAY_DEPLOY_GLIDE_MS, REPLAY_DEPLOY_STAGGER_MS, DEPLOY_LIFT_MS } from '../../../rules/gameConfig'
import { BOARD_W, BOARD_H, plotTileBox } from './geometry'
import { groundTransform } from './tokens'

// ─── Hex board — drones slide, never teleport (FLIP) ─────────────────────────
//
// The square board's slide effect, on hex geometry. Every drone tile carries
// a stable data-token-key (side + corridor + token uid) and its current slot
// (data-token = side-c-p); its ground shadow in the SVG carries the same key
// (data-shadow-key). After every render this layout effect compares where
// each key stands against the previous render and animates the difference,
// tile and shadow together:
//
//   • a token whose slot changed within its corridor (a drone sliding
//     forward to close a gap, the settle after a replay beat) glides from
//     its old slot into the new one — SLIDE_MS ease-out
//   • a cross-corridor move gets a fresh uid in the destination, so a move
//     (or its undo) registers as ONE token vanishing from a corridor while
//     ONE appears beside it: that lone appear/disappear pair in adjacent
//     same-side corridors hops the arrival in from the departed tile's slot
//     — HOP_MS ease-in-out, symmetric so a one-pitch hop reads as a flight
//     and not a teleport. A drag-and-drop move has already carried the tile
//     to the neighbour under the finger, so its arrival starts from where
//     the finger let go (dropRef, set by the board at release) and snaps the
//     last stretch in DROP_SNAP_MS. The drop is consumed by that hop, or
//     dropped once the departed tile is gone without one or it has gone
//     stale (DROP_TTL_MS — a refused move leaves the tile standing)
//   • the opponent's replay flags fresh deploys (buildOppReplaySteps sets
//     entrance:'deploy' on the token): each glides in from its side's rear
//     edge — their supply line — staggered so every arrival reads on its own
//   • a token the board has booked an arrival for (arrivalsRef: key → { x,
//     y, duration } in board units — the two veterans an UNDO BASE gives
//     back, rising from the pad the ghost stood on) glides in from that
//     point. Bookings are consumed by the arrival, or dropped once stale
//     (ARRIVAL_TTL_MS)
//   • LIFT-OFF: a token that was on the ground (data-kind 'green') and now
//     isn't — the same uid matured into a veteran at its player's submit
//     (yours at SUBMIT, the opponent's on their replay's settle beat) —
//     flies up: its block grows from the grounded transform (tokens.js
//     groundTransform: sunk and shrunk) to full flying size, DEPLOY_LIFT_MS
//     with a soft overshoot-free ease-out, while the altitude shadow the
//     board now casts for it fades in beneath. The tile itself is left
//     alone (its transform belongs to the slides above), so a lift and a
//     settle slide can play together. The block wears .hexb__lifting for
//     the lift (HexBoard.css): its fill turns from the new-unit yellow to
//     its side's colour — your amber, the opponent's red — on the same
//     clock, so a drone becomes a veteran as it rises. An airdropped drone
//     (data-delivered) keeps its own tile look until the lift, so it takes
//     the rise without the colour turn.
//
// A resupply tile waiting on its crate (visibility hidden) never moves.
// LAYOUT effect, deliberately: a delayed glide holds its tile at the start
// point (backwards fill) BEFORE the browser paints the new board; in a plain
// effect the first frame shows the tiles standing on their landing slots,
// then they snap away and glide in.
//
// Translations are computed in board units (the tiles are laid out in
// percentages of the board) and scaled to pixels for the DOM tile; the SVG
// shadow takes the board units directly (CSS px on an SVG element are user
// units). jsdom has no Element.animate — then nothing animates, and the
// board is simply right.

export const SLIDE_MS     = 280
export const HOP_MS       = 480
export const DROP_SNAP_MS = 200
export const RISE_MS      = 520
export const LIFT_MS      = DEPLOY_LIFT_MS
export const LIFT_EASING  = 'cubic-bezier(0.2, 0.8, 0.3, 1)'
const DROP_TTL_MS    = 1000
const ARRIVAL_TTL_MS = 1000
// A deploy glide starts fully outside the board, past its rear edge.
const OFF_EDGE = 8

export function useTokenFlip({ boardRef, dropRef, arrivalsRef = null }) {
    const prevRef = useRef(new Map())
    useLayoutEffect(() => {
        const board = boardRef.current
        if (!board) return
        const now = Date.now()
        const drop = dropRef?.current && now - dropRef.current.at <= DROP_TTL_MS ? dropRef.current : null
        if (dropRef) dropRef.current = drop
        const arrivals = arrivalsRef?.current ?? null
        if (arrivals) for (const [k, a] of arrivals) if (now - a.at > ARRIVAL_TTL_MS) arrivals.delete(k)
        const scale = board.clientWidth / BOARD_W || 1
        const prev = prevRef.current
        const next = new Map()

        const glide = (pos, dxU, dyU, duration, easing = 'ease-out', delay = 0) => {
            const { el, key } = pos
            if (typeof el.animate !== 'function') return
            const opts = { duration, easing, delay, fill: 'backwards' }
            el.style.zIndex = '5'
            const anim = el.animate(
                [{ transform: `translate(${dxU * scale}px, ${dyU * scale}px)` }, { transform: 'translate(0, 0)' }],
                opts,
            )
            anim.onfinish = () => { el.style.zIndex = '' }
            const shadow = board.querySelector(`[data-shadow-key="${key}"]`)
            if (shadow && typeof shadow.animate === 'function') {
                shadow.animate([{ transform: `translate(${dxU}px, ${dyU}px)` }, { transform: 'translate(0, 0)' }], opts)
            }
        }

        // Lift-off: the block inside the tile grows from grounded to flying
        // while the fresh altitude shadow fades in.
        const lift = ({ el, key }, { colourTurn = true } = {}) => {
            const block = el.firstElementChild
            if (!block || typeof block.animate !== 'function') return
            const opts = { duration: LIFT_MS, easing: LIFT_EASING, fill: 'backwards' }
            el.style.zIndex = '5'
            if (colourTurn) {
                block.classList.add('hexb__lifting')
                block.style.setProperty('--lift-ms', `${LIFT_MS}ms`)
            }
            const anim = block.animate([{ transform: groundTransform }, { transform: 'none' }], opts)
            const settle = () => { el.style.zIndex = ''; block.classList.remove('hexb__lifting') }
            anim.onfinish = settle
            setTimeout(settle, LIFT_MS + 50)
            const shadow = board.querySelector(`[data-shadow-key="${key}"]`)
            if (shadow && typeof shadow.animate === 'function') {
                shadow.animate([{ opacity: 0 }, { opacity: 1 }], { duration: LIFT_MS, easing: 'ease-out', fill: 'backwards' })
            }
        }

        const appeared = []
        board.querySelectorAll('[data-token-key]').forEach(el => {
            const key = el.dataset.tokenKey
            const [side, cStr, pStr] = (el.dataset.token ?? '').split('-')
            const c = Number(cStr), p = Number(pStr)
            if (!side || Number.isNaN(c) || Number.isNaN(p)) return
            const pos = { key, el, side, c, p, kind: el.dataset.kind, delivered: el.dataset.delivered === 'true', box: plotTileBox(side, c, p) }
            next.set(key, pos)
            if (el.style.visibility === 'hidden') return
            const was = prev.get(key)
            if (!was) appeared.push(pos)
            else {
                if (was.side === side && was.c === c && was.p !== p) {
                    glide(pos, was.box.cx - pos.box.cx, was.box.cy - pos.box.cy, SLIDE_MS)
                }
                if (was.kind === 'green' && pos.kind !== 'green') lift(pos, { colourTurn: !was.delivered })
            }
        })
        const disappeared = [...prev.values()].filter(pos => !next.has(pos.key))
        prevRef.current = next
        // A drop whose tile has left without a hop is spent.
        if (drop && dropRef && !next.has(drop.key) && !disappeared.some(d => d.key === drop.key)) dropRef.current = null

        if (appeared.length === 1 && disappeared.length === 1) {
            const a = appeared[0], d = disappeared[0]
            if (a.side === d.side && Math.abs(a.c - d.c) === 1) {
                const dx = d.box.cx - a.box.cx, dy = d.box.cy - a.box.cy
                if (drop && drop.key === d.key) {
                    dropRef.current = null
                    glide(a, dx + drop.dx / scale, dy + drop.dy / scale, DROP_SNAP_MS)
                } else glide(a, dx, dy, HOP_MS, 'ease-in-out')
                return
            }
        }
        if (drop && dropRef && disappeared.some(d => d.key === drop.key)) dropRef.current = null
        // Booked arrivals: the tile glides in from the point the board named.
        if (arrivals?.size) {
            for (const a of appeared) {
                const booking = arrivals.get(a.key)
                if (!booking) continue
                arrivals.delete(a.key)
                glide(a, booking.x - a.box.cx, booking.y - a.box.cy, booking.duration ?? RISE_MS, 'ease-in-out')
            }
        }
        appeared
            .filter(a => a.el.dataset.entrance === 'deploy')
            .forEach((a, i) => {
                const dy = a.side === 'opp'
                    ? -(a.box.top + a.box.height + OFF_EDGE)
                    : BOARD_H - a.box.top + OFF_EDGE
                glide(a, 0, dy, REPLAY_DEPLOY_GLIDE_MS, 'ease-out', i * REPLAY_DEPLOY_STAGGER_MS)
            })
    })
}
